import { awaitJSON, HROASPyLambdaFetch } from '@src/utils/fetch-utils';

/**
 * Client for the consolidated HROAS read endpoint (`GET /getHroasResults`).
 * Consumers share one react-query key per (cid, kpi, include) so the bundle is
 * fetched once and each panel applies its own `select`.
 */

export const HROAS_RESULTS_QUERY_KEY_PREFIX = 'hroasResults';

/** Today HROAS runs the "revenue" implementation only; "conversions" comes later. */
export const HROAS_DEFAULT_KPI = 'revenue';

export type HroasResultsInclude = 'all' | 'model_results' | 'ef' | 'twostageDecompositionFile';

/**
 * Include value for the twostage_decomposition_over_time.csv dataset (DEV-21662). Confirmed
 * against bpm-python-lambdas PR #193 (`_filesForInclude`, compared lowercased server-side) —
 * `twostageDecompositionFile` is the landed value, matching the response field name below.
 */
export const TWOSTAGE_DECOMPOSITION_INCLUDE: HroasResultsInclude = 'twostageDecompositionFile';

/** One serialized CSV, in the dataframeToDictionary shape the frontend expects. */
export interface HroasDataset {
  columns: { accessorKey: string; header: string; type: string }[];
  rows: Record<string, unknown>[];
}

/** One entry from the run's inputs.json models[] (the "which models?" panel). */
export interface HroasResultsModel {
  model_type: string;
  model_scope: string;
  // Taxonomy-facing model type (e.g. "dtc", "commerce", "geo", "brand_equity").
  // Absent on older runs; those models are skipped when slots are derived.
  taxonomy_model_type?: string;
  regional_scope?: string;
  decay_key?: string;
  model_run_timestamp?: string;
  s3_keys?: Record<string, unknown>;
  // s3_keys entries Scenario Planning needs but this model lacks; empty/absent = ready.
  sp_missing_artifacts?: string[];
}

/** Run-level econ-uploaded inputs from inputs.json (bucket-relative S3 keys). */
export interface HroasEconInputs {
  raw_decay_data?: string;
}

export interface HroasResultsBundle {
  cid: string;
  hroasKpi: string;
  runId: string | null;
  isLatest: boolean;
  models: HroasResultsModel[];
  econInputs: HroasEconInputs;
  data: {
    spendEffectShare: HroasDataset | null;
    responseCurves: HroasDataset | null;
    modelOverview: HroasDataset | null;
    marginalDecisionData: HroasDataset | null;
    combinedBudgetLinear: HroasDataset | null;
    combinedHolisticWeeklyAttribution: HroasDataset | null;
    /**
     * Response field for twostage_decomposition_over_time.csv (DEV-21662), served via
     * `include=twostageDecompositionFile` (bpm-python-lambdas PR #193). Deliberately distinct from
     * the unrelated `twostageDecomposition` key on the `getSpendEffectShare` endpoint (same
     * underlying CSV, different — live-compute vs. static-S3-read — serving mechanism).
     */
    twostageDecompositionFile?: HroasDataset | null;
  };
  warnings: string[];
}

export const TWOSTAGE_DECOMPOSITION_DATA_KEY: keyof HroasResultsBundle['data'] =
  'twostageDecompositionFile';

/**
 * Shared query key — identical across consumers so react-query fetches once.
 * `runId` is part of the key so selecting a different run re-fetches that run's
 * bundle instead of returning a cache hit for the previous run.
 */
export const getHroasResultsQueryKey = (
  company: string,
  hroasKpi: string = HROAS_DEFAULT_KPI,
  include: HroasResultsInclude = 'all',
  runId: string | null = null
): readonly unknown[] => [
  HROAS_RESULTS_QUERY_KEY_PREFIX,
  company,
  hroasKpi,
  include,
  runId ?? null
];

export const fetchHroasResults = async (
  company: string,
  hroasKpi: string = HROAS_DEFAULT_KPI,
  include: HroasResultsInclude = 'all',
  runId: string | null = null
): Promise<HroasResultsBundle> => {
  // Only include `runId` when set so the `latest/` fallback path remains
  // reachable; production always passes an explicit run id.
  const params: Record<string, string> = { cid: company, kpi: hroasKpi, include };
  if (runId) {
    params.runId = runId;
  }
  const res = await HROASPyLambdaFetch('/getHroasResults', {
    method: 'GET',
    params
  });
  return awaitJSON<HroasResultsBundle>(res);
};

/** Rows for one dataset, or [] when the file was absent from the run. */
export const datasetRows = (dataset: HroasDataset | null): Record<string, unknown>[] =>
  dataset?.rows ?? [];

/**
 * Bucket-relative S3 key of the latest spend_effect_share.csv (a getDecayCurves input).
 * Matches the backend's `latest/` mirror (`_runPrefix` in results.py); the fallback used
 * when no run is selected.
 */
export const latestSpendEffectShareKey = (
  company: string,
  hroasKpi: string = HROAS_DEFAULT_KPI
): string => `hroas/${company}/${hroasKpi}/latest/spend_effect_share.csv`;

/**
 * Bucket-relative S3 key of a specific run's spend_effect_share.csv (a getDecayCurves input).
 * Mirrors the exact location getHroasResults serves SES from (`_runPrefix(cid, kpi, runId)` =
 * `hroas/{cid}/{kpi}/runs/{runId}/` in results.py), so the decay panel reads the same run's SES
 * the rest of Model Results reads.
 */
export const runScopedSpendEffectShareKey = (
  company: string,
  hroasKpi: string,
  runId: string
): string => `hroas/${company}/${hroasKpi}/runs/${runId}/spend_effect_share.csv`;
